import type { Role, PopulatedUser } from '../types';

function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str = String(value);

  // Prevent CSV injection
  if (/^[=+\-@\t\r]/.test(str)) {
    str = "'" + str;
  }

  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-CA');
  } catch {
    return '';
  }
}

function getCreatorName(createdBy: string | PopulatedUser | undefined): string {
  if (!createdBy) return '';
  if (typeof createdBy === 'string') return createdBy;
  if (typeof createdBy === 'object' && '_id' in createdBy) {
    const name = [createdBy.firstname, createdBy.lastname].filter(Boolean).join(' ');
    return name || createdBy.email || createdBy._id;
  }
  return '';
}

function formatPermissionCount(role: Role): string {
  const allow = role.permissions.filter((p) => p.v === 'allow').length;
  const deny = role.permissions.filter((p) => p.v === 'deny').length;
  const parts: string[] = [];
  if (allow > 0) parts.push(`${allow} allow`);
  if (deny > 0) parts.push(`${deny} deny`);
  return parts.join(', ') || '0';
}

const COLUMNS = [
  { key: 'roleId', label: 'Role ID', getValue: (r: Role) => r.roleId },
  { key: 'permissions', label: 'Permissions', getValue: (r: Role) => formatPermissionCount(r) },
  { key: 'createdBy', label: 'Created By', getValue: (r: Role) => getCreatorName(r.created_by) },
  { key: 'createdAt', label: 'Created At', getValue: (r: Role) => formatDate(r.created_at) },
];

function generateCSVContent(roles: Role[]): string {
  const header = COLUMNS.map((c) => escapeCSVValue(c.label)).join(',');
  const rows = roles.map((role) =>
    COLUMNS.map((c) => escapeCSVValue(c.getValue(role))).join(',')
  );
  return [header, ...rows].join('\n');
}

export function exportRolesToCSV(roles: Role[], filename = 'roles'): void {
  const csvContent = generateCSVContent(roles);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function copyRolesToClipboard(roles: Role[]): Promise<void> {
  const csvContent = generateCSVContent(roles);
  await navigator.clipboard.writeText(csvContent);
}
